import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { WorkingHoursForm } from "../components/WorkingHoursForm";
import { useAvailabilities } from "../hooks/useAvailabilities";

interface WorkingHoursScope {
  userId: string;
  businessId: string;
  locationId: string;
  businessTimezone: string;
}

function WorkingHoursLoader(props: WorkingHoursScope) {
  const { userId, businessId, locationId } = props;
  const query = useAvailabilities({ userId, businessId, locationId });

  if (query.data === undefined) {
    return (
      <Page>
        <PageHeader title="Working hours" />
        <PageContent>
          {query.error ? (
            <ErrorState
              title="Couldn't load working hours"
              message={query.error.message}
              onRetry={() => void query.refetch()}
            />
          ) : <LoadingSpinner message="Loading working hours..." />}
        </PageContent>
      </Page>
    );
  }

  return (
    <WorkingHoursForm
      {...props}
      initialAvailability={query.data[0]}
      refreshError={query.error?.message}
      onRetry={() => void query.refetch()}
    />
  );
}

export function AvailabilitiesPage() {
  const { user } = useAuth();
  const { business, businessId, locationId, loading } = useCurrentBusiness();

  if (loading || !user?.id || !businessId || !locationId || !business) {
    return (
      <Page>
        <PageHeader title="Working hours" />
        <PageContent>
          {loading ? <LoadingSpinner message="Loading business..." /> : (
            <ErrorState message="Select a business and location to manage working hours." />
          )}
        </PageContent>
      </Page>
    );
  }

  return (
    <WorkingHoursLoader
      key={`${user.id}:${businessId}:${locationId}`}
      userId={user.id}
      businessId={businessId}
      locationId={locationId}
      businessTimezone={business.timezone}
    />
  );
}
